import { get } from 'lodash';
import { hasCode } from '../../schema/widgets/ICodeWidget.ts';
import { type ContainerOrRootWidget } from '../../schema/widgets/repository/containers/ContainerOrRootWidget.ts';
import { hasChildren } from '../../schema/widgets/repository/containers/hasChildren.ts';
import {
  isContainer,
  isContainerData,
} from '../../schema/widgets/repository/containers/IContainerWidget.ts';
import {
  type IRootWidget,
  isRoot,
  isRootData,
} from '../../schema/widgets/repository/containers/root/IRootWidget.ts';
import {
  type ISortableWidgetData,
  type Widget,
  type WidgetOrRoot,
  WidgetType,
} from '../../schema/widgets/Widget.ts';

export class TreeNavigatorLogic {
  /**
   * Funzione che esplora l'albero nodo per nodo.
   * @param root Nodo radice dell'albero
   * @param callback Funzione da eseguire per ogni nodo. Se la funzione ritorna `false`, l'esplorazione si interrompe.
   */
  static forEachNode(
    root: WidgetOrRoot,
    callback: (
      node: Widget,
      index: number,
      parent: ContainerOrRootWidget | null,
      // biome-ignore lint/suspicious/noConfusingVoidType: <tdb>
    ) => boolean | void,
  ) {
    function explore(
      node: WidgetOrRoot,
      parent: ContainerOrRootWidget | null = null,
      index = 0,
    ) {
      if (!isRoot(node)) {
        const shouldSkip = callback(node, index, parent);
        if (shouldSkip === true) return;
      }
      if (hasChildren(node)) {
        node.children?.forEach(
          (child, index) => child && explore(child, node, index),
        );
      }
    }
    explore(root);
  }

  /**
   * Ritorna vero se il nodo è un container
   */
  static isContainerOrRoot(node: ISortableWidgetData) {
    return isContainerData(node) || isRootData(node);
  }

  /** Ritorna il path dalla root al nodo selezionato sotto forma di stringa */
  static getNodePath(node: ISortableWidgetData): string {
    return node.path?.concat('children', node.order!).join('.') ?? '';
  }

  /** Ritorna il path dalla root al parent del nodo selezionato sotto forma di stringa */
  static getParentPath(node: ISortableWidgetData) {
    return node.path?.join('.') ?? '';
  }

  /** Ritorna il path completo dalla root alla lista di children del nodo selezionato, sotto forma di stringa */
  static getChildrenPath(node: ISortableWidgetData) {
    const path = TreeNavigatorLogic.getNodePath(node);
    if (path.length === 0) return 'children';
    return path + '.children';
  }

  /** Ritorna il nodo richiesto seguendo il path dalla root */
  static getNode(
    root: WidgetOrRoot,
    node: ISortableWidgetData,
  ): Widget | IRootWidget | null {
    return get(root, TreeNavigatorLogic.getNodePath(node)) ?? root;
  }

  /** Ritorna il nodo padre del nodo selezionato, seguendo il path dalla root */
  static getParentNode(
    root: ContainerOrRootWidget,
    node: ISortableWidgetData,
  ): ContainerOrRootWidget {
    return get(root, TreeNavigatorLogic.getParentPath(node)) ?? root;
  }

  /** Ritorna il nodo con il codice richiesto */
  static getNodeByCode(
    root: ContainerOrRootWidget,
    code: string,
  ): Widget | null {
    let result = null;
    TreeNavigatorLogic.forEachNode(root, node => {
      if (hasCode(node) && node.code === code) {
        result = node;
        return true;
      }
    });
    return result;
  }

  /**
   * Dato l'albero e il path, ritorna il primo nodo container.
   * - Se il path porta a un container, allora ritorna il nodo corrispondente
   * - Se il path porta a un nodo foglia, allora ritorna il nodo padre (che è sicuramente un container)
   */
  static getFirstContainer(
    root: IRootWidget,
    targetNode: ISortableWidgetData,
  ): ContainerOrRootWidget | null {
    // Se il target è un container, allora ritorna il target
    if (
      // Se è un extra droppable allora non voglio il target ma il suo parent
      !TreeNavigatorLogic.isExtraDroppable(targetNode) &&
      TreeNavigatorLogic.isContainerOrRoot(targetNode)
    ) {
      return TreeNavigatorLogic.getNode(
        root,
        targetNode,
      ) as ContainerOrRootWidget | null;
    }

    // Se il target non è un container, allora ritorno il suo parent (che è sicuramente un container)
    return TreeNavigatorLogic.getParentNode(root, targetNode);
  }

  /**
   * Ritorna vero se il nodo è in realtà una zona extra droppable,
   * come quelle per i container atte a indicare il posizionamento
   * sopra/sotto.
   */
  static isExtraDroppable(node: ISortableWidgetData) {
    return Boolean(node.directionalDroppableType);
  }

  /**
   * Calcola il path e l'ordine di ogni nodo dell'albero.
   * N.B. Fa side effect sul tree passato come parametro
   */
  static computeOrder<T extends WidgetOrRoot>(root: T): T {
    TreeNavigatorLogic.forEachNode(root, (node, index, parent) => {
      if (!node) {
        console.warn("Node doesn't exist", node, index, parent);
        return;
      }

      // Le sottosezioni hanno un ordine calcolato al loro interno, quindi
      // i _figli_ delle sottosezioni non devono essere aggiornati.
      if (parent?.type === WidgetType.Subsection) return;

      // per ogni nodo calcolo il path del nodo in base al padre
      node.path =
        !isRoot(parent) && parent?.path
          ? [...parent.path, 'children', parent.order!]
          : [];
      node.order = index;
    });
    return root;
  }

  /**
   * Ritorna l'albero sotto forma di array di nodi
   */
  static getFlattenItems(root: WidgetOrRoot) {
    const result = [] as Widget[];
    TreeNavigatorLogic.forEachNode(root, node => {
      result.push(node);
    });
    return result;
  }

  /**
   * Dati i due array di nodi, sposta il nodo `active` nell'array `targetNodeSiblings`.
   */
  static moveNode(
    activePosition: number,
    overPosition: number,
    sourceSiblings: Widget[],
    targetNodeSiblings: Widget[],
    /** Indica se i due array corrispondono allo stesso container */
    sameContainer: boolean,
  ) {
    // Rimuovo il nodo dalla lista di source
    const activeNode = sourceSiblings.splice(activePosition, 1);
    let spliceStart = overPosition;

    // Se si è nello stesso container e l'active proviene da una posizione successiva all'over
    // riduco di uno lo spliceStart, altrimenti il nodo viene inserito troppo avanti
    // (causato dal fatto che abbiamo rimosso precedentemente il nodo quindi gli indici sono cambiati)
    if (sameContainer && overPosition > activePosition) {
      spliceStart -= 1;
    }

    targetNodeSiblings.splice(spliceStart, 0, activeNode[0]);
  }

  /**
   * Ottiene un nodo dall'albero dato il suo id
   */
  // TODO: Sostituire con un algoritmo di update del `tree` immutabile, ad
  // esempio con immer.js
  static byId(root: IRootWidget, id: string): Widget | null {
    const queue: Widget[] = [...root.children];
    let headIdx = 0;
    while (headIdx < queue.length) {
      const node = queue[headIdx++];
      if (node.id === id || node.fromVirtualId === id) return node;
      if (hasChildren(node) && node.children) {
        queue.push(...node.children);
      }
    }
    return null;
  }

  /**
   * Ritorna vero se il nodo è contenuto o è lo stesso nodo di
   * `potentialAncestor`. Utilizzato per il drag and drop.
   */
  static isInsideOrSame(
    node: ISortableWidgetData,
    potentialAncestor: ISortableWidgetData,
  ) {
    const nodePath = TreeNavigatorLogic.getNodePath(node);
    const ancestorPath = TreeNavigatorLogic.getNodePath(potentialAncestor);
    return nodePath.startsWith(ancestorPath);
  }

  static remove(root: IRootWidget, node: Widget) {
    const parent = TreeNavigatorLogic.getParentNode(root, node);
    if (!parent) return;
    if (!isContainer(parent) && !isRoot(parent)) return;
    parent.children?.splice(node.order!, 1);
  }
}
